import type { SignInPage } from '@server/shared-sign-in';
import { useQuery } from '@tanstack/react-query';

import { RudderstackBrandLogo } from '@/_shared/components/RudderstackBrandLogo';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { orpc } from '@/_shared/lib/orpc';

import { NativeSignIn } from './nativeSignIn/NativeSignIn';

/**
 * The sign-in controls, shared by `/login` and `/invite/:id` so the two doors cannot drift.
 * Owns the provider probe end to end — including its loading skeleton — so a caller cannot
 * gate this component on a second observer of the same query.
 */
export function SignInProviders({
  returnTo,
  invite = false,
  fromWebApp = false,
  page = 'login',
  onNewAttempt,
}: {
  returnTo?: string | null;
  /** The visitor came from a Lookout invite. */
  invite?: boolean;
  /** The visitor came from the RudderStack web app's sidebar. Only Lookout's own sign-in uses it. */
  fromWebApp?: boolean;
  /** The page that shows these controls. A Google or SSO error returns to it. */
  page?: SignInPage;
  /** Called when a form here is sent or Google starts, so the page can hide the error of an earlier attempt. */
  onNewAttempt?: () => void;
}): React.ReactElement {
  const { data: providers, isLoading } = useQuery(orpc.auth.providers.queryOptions());
  const nativeSignIn = providers?.nativeSignIn ?? null;

  const withReturn = (path: string): string => (returnTo ? `${path}?return_to=${encodeURIComponent(returnTo)}` : path);

  if (isLoading) {
    return <Skeleton className="h-8 w-full rounded-md" />;
  }

  return (
    <div className="flex flex-col gap-3" onSubmitCapture={onNewAttempt}>
      {/* Lookout's own sign-in replaces the web app's consent page (ACT2-871). */}
      {nativeSignIn ? (
        <NativeSignIn
          config={nativeSignIn}
          returnTo={returnTo ?? null}
          invite={invite}
          fromWebApp={fromWebApp}
          page={page}
          onNewAttempt={onNewAttempt}
        />
      ) : (
        providers?.rudderstack && (
          <Button variant="primary" size="lg" className="w-full gap-2" asChild>
            <a href={withReturn('/auth/rudderstack')}>
              <RudderstackBrandLogo variant="compact" className="size-4 text-primary-foreground" />
              Continue with RudderStack
            </a>
          </Button>
        )
      )}

      {/* No provider means no way in. Covers a misconfigured local env AND a failed provider
          probe — an unset `providers` must not fall through to an empty card. Names no
          environment variable: this page is public, so operator detail belongs in the log. */}
      {!nativeSignIn &&
        !providers?.rudderstack &&
        (providers?.localDevelopment ? (
          <p className="text-xs text-muted-foreground">
            Sign-in needs RudderStack OAuth. Run Lookout locally with <code>mini lookout &lt;workspace&gt;</code>{' '}
            (rudder-mini).
          </p>
        ) : (
          <Callout variant="error" className="text-left">
            <p>Sign-in is unavailable.</p>
            <p className="mt-1 text-xs">
              This deployment has no login provider configured. Please contact your administrator.
            </p>
          </Callout>
        ))}
    </div>
  );
}
